/**
 * YouTube Playlist Import Service
 * Handles importing YouTube playlists and creating sheets/setlists from them
 */

class YouTubePlaylistService {
  constructor() {
    this.apiKey = null; // Would need YouTube API key for full functionality
  }

  /**
   * Extract playlist ID from YouTube playlist URL
   * Supports formats like:
   * - https://www.youtube.com/playlist?list=PLrxfgDEc2NxY_fRjHm4_GUlEH7BusAKcS
   * - https://youtube.com/playlist?list=PLrxfgDEc2NxY_fRjHm4_GUlEH7BusAKcS
   */
  extractPlaylistId(url) {
    if (!url) return null;
    
    const patterns = [
      /[?&]list=([a-zA-Z0-9_-]+)/,
      /youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/,
      /youtu\.be\/playlist\?list=([a-zA-Z0-9_-]+)/,
      /youtube\.com\/watch\?.*[?&]list=([a-zA-Z0-9_-]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  }

  /**
   * Extract video information from YouTube video URL
   * Attempts to parse title and artist from video title
   */
  parseVideoTitle(title) {
    if (!title) return { title: 'Unknown Title', artist: 'Unknown Artist' };
    
    // Simply use the full video title as-is and set artist as Unknown
    // This avoids incorrect parsing since YouTube titles don't follow a standard format
    return { 
      title: title.trim(), 
      artist: 'Unknown Artist' 
    };
  }

  /**
   * Fetch playlist information using YouTube API (requires API key)
   * For now, this is a placeholder that would need actual API implementation
   */
  async fetchPlaylistData(playlistId) {
    if (!this.apiKey) {
      // For now, we'll use a public API approach or fetch from backend
      // In production, you'd want to store the API key on your backend
      throw new Error('YouTube API key not configured. Please set up YouTube API integration.');
    }
    
    const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${playlistId}&key=${this.apiKey}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`YouTube API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      return data.items.map(item => ({
        title: item.snippet.title,
        videoId: item.snippet.resourceId.videoId,
        url: `https://www.youtube.com/watch?v=${item.snippet.resourceId.videoId}`,
        description: item.snippet.description,
        thumbnails: item.snippet.thumbnails
      }));
      
    } catch (error) {
      console.error('YouTube API fetch error:', error);
      throw error;
    }
  }

  /**
   * Fetch playlist videos using YouTube API
   * This is an alias for fetchPlaylistData for consistency
   */
  async fetchPlaylistVideos(playlistId) {
    return await this.fetchPlaylistData(playlistId);
  }

  /**
   * Set YouTube API key
   * In production, this should be called from your backend or environment config
   */
  setApiKey(apiKey) {
    this.apiKey = apiKey;
  }

  /**
   * Try to fetch playlist data from backend API instead of direct YouTube API
   * This is more secure as it keeps the API key on the server
   */
  async fetchPlaylistFromBackend(playlistId) {
    try {
      // Import fetchWithAuth to get proper authentication  
      const { fetchWithAuth, API_URL } = await import('./ApiService');
      
      const response = await fetchWithAuth(`${API_URL}/youtube/playlist/${playlistId}`, {
        method: 'GET'
      });

      return response.videos || [];
    } catch (error) {
      console.error('Backend playlist fetch error:', error);
      throw error;
    }
  }

  /**
   * Alternative method: Parse playlist from user input
   * User provides playlist URL and we guide them through manual entry
   * or we use a different approach like scraping (not recommended for production)
   */
  async createPlaylistFromUserInput(playlistUrl, playlistName, videoData = null) {
    const playlistId = this.extractPlaylistId(playlistUrl);
    if (!playlistId) {
      throw new Error('Invalid YouTube playlist URL');
    }

    let videos = [];
    
    // Try multiple approaches to get playlist data
    try {
      // First try: Backend API (most secure)
      console.log('Trying to fetch playlist from backend...');
      videos = await this.fetchPlaylistFromBackend(playlistId);
      console.log(`Backend fetch successful: ${videos.length} videos`);
    } catch (backendError) {
      console.log('Backend fetch failed:', backendError.message);
      
      try {
        // Second try: Direct YouTube API (if API key is available)
        console.log('Trying direct YouTube API...');
        videos = await this.fetchPlaylistVideos(playlistId);
        console.log(`Direct API fetch successful: ${videos.length} videos`);
      } catch (apiError) {
        console.log('Direct API fetch failed:', apiError.message);
        
        // Third try: Use provided manual data as fallback
        if (videoData && videoData.length > 0) {
          console.log('Using provided manual video data...');
          videos = videoData.map(video => ({
            title: video.title,
            url: video.url
          }));
        } else {
          // All methods failed
          throw new Error(
            'Failed to fetch playlist data automatically. This could be because:\n' +
            '• The playlist is private\n' +
            '• YouTube API is not configured\n' +
            '• Network connectivity issues\n\n' +
            'Please check the playlist URL and try again.'
          );
        }
      }
    }

    if (videos.length === 0) {
      throw new Error('No videos found in playlist');
    }

    console.log(`Processing ${videos.length} videos from playlist...`);

    // Create sheets from video data
    const sheets = [];
    const { saveSheet } = await import('./SheetStorageService');
    
    for (const video of videos) {
      const { title, artist } = this.parseVideoTitle(video.title);
      
      // Create a basic sheet structure
      const sheetData = {
        title: title,
        artist: artist,
        mediaUrl: video.url,
        status: 'WIP',
        bpm: null,
        sections: [], // Empty sections - user can add content later
        dateCreated: new Date(),
        dateModified: new Date()
      };
      
      try {
        const savedSheet = await saveSheet(sheetData, true);
        sheets.push(savedSheet);
        console.log(`Created sheet: ${title} by ${artist}`);
      } catch (error) {
        console.error(`Failed to create sheet for ${title}:`, error);
        // Continue with other songs even if one fails
      }
    }

    // Create setlist with the sheets
    const { createSetlist } = await import('./SetlistStorageService');
    
    const setlistData = {
      name: playlistName || `Imported Playlist (${new Date().toLocaleDateString()})`,
      description: `Imported from <a href="${playlistUrl}" target="_blank" rel="noopener noreferrer">YouTube</a>`,
      sheets: sheets,
      dateCreated: new Date(),
      dateModified: new Date()
    };

    const setlist = await createSetlist(setlistData);
    
    if (!setlist) {
      throw new Error('Failed to create setlist. Please make sure you are logged in and try again.');
    }
    
    console.log(`Created setlist "${setlist.name}" with ${sheets.length} sheets`);
    
    return {
      setlist: setlist,
      sheets: sheets,
      summary: {
        totalVideos: videos.length,
        successfulSheets: sheets.length,
        failedSheets: videos.length - sheets.length
      }
    };
  }

  /**
   * Validate a YouTube playlist URL
   */
  isValidPlaylistUrl(url) {
    return this.extractPlaylistId(url) !== null;
  }
}

// Export singleton instance
const youTubePlaylistService = new YouTubePlaylistService();
export default youTubePlaylistService;